import React, { useState } from 'react';
import { Box, Divider, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { IconButton, useMediaDevice } from '@travelity/ui';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    convertNameDtoToFullName,
    ProductType,
    ResourceType,
    Team,
    useRemoveTeam,
    UserMembershipStatus,
} from '@travelity/api';
import { useTranslation } from 'react-i18next';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { useToast } from '../../../hooks';

import { useResourcePermissions } from '../../../contexts/user';
import { TeamModal } from '../../../components/team-modal';
import { ReasonModal } from '../../../components/booking-item/components/reason-modal';

export interface TeamDetailsProps {
    isLoading: boolean;
    team?: Team;
}

export const TeamDetails: React.FC<TeamDetailsProps> = React.memo(
    (props: TeamDetailsProps) => {
        const { isLoading, team } = props;
        const { showError, showSuccess } = useToast();
        const { t } = useTranslation();
        const [inviteMember, setInviteMember] = useState(false);
        const [deletingTeam, setDeletingTeam] = useState<string>();
        const navigate = useNavigate();

        const { mutate: removeTeam, isLoading: isRemoveTeamLoading } =
            useRemoveTeam({
                onSuccess: () => {
                    setDeletingTeam(undefined);
                    navigate(0);
                    showSuccess('REMOVE_TEAM_SUCCESS', {
                        name: team?.name,
                    });
                },
                onError: () => {
                    showError('REMOVE_TEAM_FAILURE', {
                        name: team?.name,
                    });
                },
            });

        const permissions = useResourcePermissions(ResourceType.TEAM);
        const owner = team?.createdBy
            ? convertNameDtoToFullName(team?.createdBy)
            : undefined;
        const registeredMembers = team?.members.filter(
            member => member.membershipStatus === UserMembershipStatus.ACTIVE
        ).length;

        const { isMobile } = useMediaDevice();

        return (
            <Grid
                container
                sx={{ py: { xs: 1, sm: 2 }, px: { xs: 1, sm: 0 }, height: 180 }}
            >
                <Grid sm={6}>
                    <Typography variant="h5" sx={{ color: '#C0C4CE' }}>
                        <Box
                            component="span"
                            sx={{
                                color: '#2B395B',
                                fontSize: '24px',
                                fontWeight: 700,
                            }}
                        >
                            Team {team?.name}
                        </Box>
                    </Typography>
                    {!isMobile && (
                        <Stack direction="row" gap={1} sx={{ mt: 1 }}>
                            <IconButton
                                onClick={() => setInviteMember(true)}
                                withShadow
                                icon={<AddBoxIcon fontSize="small" />}
                                tooltip="Invite"
                                tooltipPlacement="bottom"
                                disabled={!team?.permissions.update}
                            />
                            {permissions.delete && (
                                <IconButton
                                    icon={<DeleteIcon fontSize="small" />}
                                    withShadow
                                    hoverColor="error.main"
                                    onClick={() => setDeletingTeam(team?.id)}
                                    disabled={!team?.permissions.delete}
                                    tooltip="Cancel"
                                    tooltipPlacement="bottom"
                                />
                            )}
                        </Stack>
                    )}
                </Grid>
                <Grid xs={12} sm={6}>
                    <Stack
                        direction="row"
                        sx={{
                            my: 1,
                            mx: { xs: 0, sm: 1 },
                            borderRadius: '12px',
                            bgcolor: '#F4F6FA',
                        }}
                    >
                        <Stack
                            justifyContent="center"
                            sx={{
                                p: 1,
                                borderRight: '#FFF 1px solid',
                                color: '#B2B9CD',
                            }}
                        >
                            <svg
                                width="14"
                                height="67"
                                viewBox="0 0 14 67"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M10.14 63.076C10.14 63.6453 10.07 64.168 9.93 64.644C9.78067 65.12 9.56133 65.5493 9.272 65.932C8.98267 66.3147 8.63267 66.6553 8.222 66.954L6.878 65.764C7.51267 65.3067 7.95133 64.8587 8.194 64.42C8.43667 63.9813 8.558 63.4867 8.558 62.936C8.558 62.6187 8.51133 62.3293 8.418 62.068C8.31533 61.8067 8.18 61.6013 8.012 61.452C7.83467 61.3027 7.634 61.228 7.41 61.228C7.25133 61.228 7.10667 61.2607 6.976 61.326C6.836 61.382 6.71467 61.4707 6.612 61.592C6.5 61.7133 6.39733 61.8627 6.304 62.04C6.21067 62.2173 6.13133 62.418 6.066 62.642C6.00067 62.866 5.94 63.1133 5.884 63.384C5.78133 63.8973 5.646 64.3453 5.478 64.728C5.30067 65.1107 5.086 65.4327 4.834 65.694C4.57267 65.9553 4.28333 66.1467 3.966 66.268C3.63933 66.3893 3.27067 66.45 2.86 66.45C2.44933 66.45 2.07133 66.3613 1.726 66.184C1.38067 65.9973 1.082 65.7453 0.829999 65.428C0.577999 65.1107 0.381999 64.742 0.241999 64.322C0.102 63.902 0.0319996 63.4493 0.0319996 62.964C0.0319996 62.4133 0.0973329 61.9233 0.228 61.494C0.358666 61.0553 0.554666 60.6727 0.815999 60.346C1.068 60.01 1.376 59.7347 1.74 59.52L2.93 60.724C2.64067 60.9107 2.40267 61.1207 2.216 61.354C2.02 61.5873 1.87533 61.8393 1.782 62.11C1.67933 62.3807 1.628 62.6653 1.628 62.964C1.628 63.3 1.67467 63.594 1.768 63.846C1.86133 64.0887 1.99667 64.28 2.174 64.42C2.342 64.56 2.54733 64.63 2.79 64.63C2.97667 64.63 3.14467 64.588 3.294 64.504C3.434 64.42 3.56 64.3033 3.672 64.154C3.784 63.9953 3.882 63.7947 3.966 63.552C4.05 63.3093 4.12467 63.034 4.19 62.726C4.29267 62.2127 4.43267 61.7507 4.61 61.34C4.778 60.9293 4.98333 60.5793 5.226 60.29C5.45933 60.0007 5.73 59.7813 6.038 59.632C6.33667 59.4827 6.67267 59.408 7.046 59.408C7.69 59.408 8.24533 59.562 8.712 59.87C9.16933 60.1687 9.524 60.5933 9.776 61.144C10.0187 61.6947 10.14 62.3387 10.14 63.076ZM10.14 55.1527C10.14 55.6847 10.0233 56.1467 9.79 56.5387C9.54733 56.9307 9.20667 57.2293 8.768 57.4347C8.32 57.64 7.788 57.7427 7.172 57.7427L2.622 57.7427L2.622 56.0207L6.808 56.0207C7.2 56.0207 7.536 55.96 7.816 55.8387C8.08667 55.7173 8.29667 55.54 8.446 55.3067C8.59533 55.0733 8.67 54.7933 8.67 54.4667C8.67 54.224 8.63267 54.0047 8.558 53.8087C8.474 53.6033 8.362 53.426 8.222 53.2767C8.07267 53.1273 7.9 53.0107 7.704 52.9267C7.49867 52.8427 7.27933 52.8007 7.046 52.8007L2.622 52.8007L2.622 51.0787L10 51.0787L10 52.7587L8.46 52.7867L8.292 52.4787C8.64667 52.5907 8.964 52.782 9.244 53.0527C9.51467 53.314 9.734 53.6267 9.902 53.9907C10.0607 54.3547 10.14 54.742 10.14 55.1527ZM10 49.0884L2.622 49.0884L2.622 47.3944L4.106 47.3664L4.218 47.6464C3.97533 47.5624 3.75133 47.4364 3.546 47.2684C3.33133 47.1004 3.14933 46.8997 3 46.6664C2.84133 46.433 2.72 46.1857 2.636 45.9244C2.54267 45.663 2.496 45.397 2.496 45.1264C2.496 44.725 2.56133 44.3704 2.692 44.0624C2.81333 43.745 3.00933 43.4837 3.28 43.2784C3.55067 43.0637 3.896 42.905 4.316 42.8024L4.26 43.0684L4.036 42.9564C3.812 42.8537 3.61133 42.709 3.434 42.5224C3.24733 42.3357 3.084 42.1257 2.944 41.8924C2.79467 41.659 2.68267 41.4164 2.608 41.1644C2.53333 40.903 2.496 40.6464 2.496 40.3944C2.496 39.8344 2.608 39.3677 2.832 38.9944C3.056 38.621 3.39667 38.341 3.854 38.1544C4.31133 37.9677 4.876 37.8744 5.548 37.8744L10 37.8744L10 39.6104L5.66 39.6104C5.28667 39.6104 4.97867 39.6617 4.736 39.7644C4.49333 39.8577 4.316 40.007 4.204 40.2124C4.08267 40.4084 4.022 40.6557 4.022 40.9544C4.022 41.1877 4.064 41.4117 4.148 41.6264C4.22267 41.8317 4.33467 42.009 4.484 42.1584C4.624 42.3077 4.78733 42.4244 4.974 42.5084C5.16067 42.5924 5.366 42.6344 5.59 42.6344L10 42.6344L10 44.3704L5.646 44.3704C5.29133 44.3704 4.99733 44.4217 4.764 44.5244C4.52133 44.627 4.33933 44.7764 4.218 44.9724C4.08733 45.1684 4.022 45.4064 4.022 45.6864C4.022 45.9197 4.064 46.139 4.148 46.3444C4.22267 46.5497 4.33 46.727 4.47 46.8764C4.61 47.0257 4.77333 47.1424 4.96 47.2264C5.14667 47.3104 5.352 47.3524 5.576 47.3524L10 47.3524L10 49.0884ZM10 35.9907L2.622 35.9907L2.622 34.2967L4.106 34.2687L4.218 34.5487C3.97533 34.4647 3.75133 34.3387 3.546 34.1707C3.33133 34.0027 3.14933 33.802 3 33.5687C2.84133 33.3354 2.72 33.088 2.636 32.8267C2.54266 32.5654 2.496 32.2994 2.496 32.0287C2.496 31.6274 2.56133 31.2727 2.692 30.9647C2.81333 30.6474 3.00933 30.386 3.28 30.1807C3.55066 29.966 3.896 29.8074 4.316 29.7047L4.26 29.9707L4.036 29.8587C3.812 29.756 3.61133 29.6114 3.434 29.4247C3.24733 29.238 3.084 29.028 2.944 28.7947C2.79466 28.5614 2.68266 28.3187 2.608 28.0667C2.53333 27.8054 2.496 27.5487 2.496 27.2967C2.496 26.7367 2.608 26.27 2.832 25.8967C3.056 25.5234 3.39666 25.2434 3.854 25.0567C4.31133 24.87 4.876 24.7767 5.548 24.7767L10 24.7767L10 26.5127L5.66 26.5127C5.28666 26.5127 4.97866 26.564 4.736 26.6667C4.49333 26.76 4.316 26.9094 4.204 27.1147C4.08266 27.3107 4.022 27.558 4.022 27.8567C4.022 28.09 4.064 28.314 4.148 28.5287C4.22266 28.734 4.33466 28.9114 4.484 29.0607C4.624 29.21 4.78733 29.3267 4.974 29.4107C5.16066 29.4947 5.366 29.5367 5.59 29.5367L10 29.5367L10 31.2727L5.646 31.2727C5.29133 31.2727 4.99733 31.324 4.764 31.4267C4.52133 31.5294 4.33933 31.6787 4.218 31.8747C4.08733 32.0707 4.022 32.3087 4.022 32.5887C4.022 32.822 4.064 33.0414 4.148 33.2467C4.22266 33.452 4.33 33.6294 4.47 33.7787C4.61 33.928 4.77333 34.0447 4.96 34.1287C5.14667 34.2127 5.352 34.2547 5.576 34.2547L10 34.2547L10 35.9907ZM10.14 20.079C10.14 20.6857 9.972 21.2364 9.636 21.731C9.3 22.2257 8.84266 22.6224 8.264 22.921C7.68533 23.2197 7.02733 23.369 6.29 23.369C5.55266 23.369 4.89466 23.2197 4.316 22.921C3.73733 22.6224 3.28466 22.2164 2.958 21.703C2.63133 21.1897 2.468 20.611 2.468 19.967C2.468 19.5937 2.524 19.253 2.636 18.945C2.73866 18.637 2.888 18.3664 3.084 18.133C3.28 17.8997 3.504 17.7084 3.756 17.559C4.008 17.4004 4.27866 17.293 4.568 17.237L4.47 17.615L2.622 17.615L2.622 15.879L10 15.879L10 17.629L8.236 17.629L8.166 17.223C8.418 17.2884 8.66533 17.4097 8.908 17.587C9.14133 17.755 9.35133 17.9697 9.538 18.231C9.71533 18.483 9.86 18.7677 9.972 19.085C10.084 19.393 10.14 19.7244 10.14 20.079ZM8.614 19.603C8.614 19.2017 8.516 18.847 8.32 18.539C8.124 18.231 7.85333 17.993 7.508 17.825C7.15333 17.6477 6.74733 17.559 6.29 17.559C5.842 17.559 5.44533 17.6477 5.1 17.825C4.75466 17.993 4.484 18.231 4.288 18.539C4.092 18.847 3.994 19.2017 3.994 19.603C3.994 20.0044 4.092 20.359 4.288 20.667C4.484 20.9657 4.75466 21.199 5.1 21.367C5.44533 21.535 5.842 21.619 6.29 21.619C6.74733 21.619 7.15333 21.535 7.508 21.367C7.85333 21.199 8.124 20.9657 8.32 20.667C8.516 20.359 8.614 20.0044 8.614 19.603ZM10 13.8826L2.622 13.8826L2.622 12.1886L4.974 12.1606L4.442 12.3986C4.06866 12.296 3.73266 12.1186 3.434 11.8666C3.13533 11.6146 2.902 11.3253 2.734 10.9986C2.55666 10.6626 2.468 10.3126 2.468 9.94862C2.468 9.78996 2.482 9.64062 2.51 9.50062C2.538 9.35129 2.57066 9.22996 2.608 9.13662L4.498 9.59862C4.442 9.70129 4.39533 9.82729 4.358 9.97662C4.32066 10.126 4.302 10.2753 4.302 10.4246C4.302 10.658 4.34866 10.882 4.442 11.0966C4.526 11.302 4.64733 11.484 4.806 11.6426C4.96466 11.8013 5.15133 11.9273 5.366 12.0206C5.57133 12.1046 5.80466 12.1466 6.066 12.1466L10 12.1466L10 13.8826ZM13.22 6.87005L9.594 5.30205L10.742 5.28805L2.622 8.92805L2.622 6.94005L7.438 4.91005C7.55933 4.84471 7.72266 4.77938 7.928 4.71405C8.124 4.64871 8.32 4.59271 8.516 4.54605L8.628 4.89605C8.45066 4.83071 8.264 4.76071 8.068 4.68605C7.86266 4.61138 7.65266 4.54138 7.438 4.47605L2.622 2.74005L2.622 0.738046L10 3.81805L13.22 5.07805L13.22 6.87005Z"
                                    fill="#B2B9CD"
                                />
                            </svg>
                        </Stack>
                        <Box sx={{ flexGrow: 2, p: 2, pb: 1 }}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ mb: 1, lineHeight: 1 }}
                            >
                                <Box
                                    sx={{
                                        color: '#6B748C',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                    }}
                                >
                                    Owner
                                </Box>
                                <Box
                                    sx={{
                                        color: '#6B758D',
                                        textAlign: 'right',
                                        fontSize: '14px',
                                        fontWeight: '400',
                                    }}
                                >
                                    {isLoading ? (
                                        <Skeleton width={120} />
                                    ) : (
                                        `${owner}`
                                    )}
                                </Box>
                            </Stack>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ mb: 1, lineHeight: 1 }}
                            >
                                <Box
                                    sx={{
                                        color: '#6B748C',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                    }}
                                >
                                    Total Members
                                </Box>
                                <Box
                                    sx={{
                                        color: '#6B758D',
                                        textAlign: 'right',
                                        fontSize: '14px',
                                        fontWeight: '400',
                                    }}
                                >
                                    {isLoading ? (
                                        <Skeleton width={60} />
                                    ) : (
                                        team?.members.length
                                    )}
                                </Box>
                            </Stack>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ mb: 1, lineHeight: 1 }}
                            >
                                <Box
                                    sx={{
                                        color: '#6B748C',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                    }}
                                >
                                    Registered Members
                                </Box>
                                <Box
                                    sx={{
                                        color: '#6B758D',
                                        textAlign: 'right',
                                        fontSize: '14px',
                                        fontWeight: '400',
                                    }}
                                >
                                    {isLoading ? (
                                        <Skeleton width={60} />
                                    ) : (
                                        registeredMembers
                                    )}
                                </Box>
                            </Stack>

                            {!!Object.keys(team?.productTypes || {}).length && (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        sx={{
                                            mb: 1,
                                            lineHeight: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                color: '#6B748C',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                            }}
                                        >
                                            Shared products
                                        </Box>
                                        <Stack
                                            sx={{
                                                color: '#6B748C',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                            }}
                                        >
                                            {Object.values(ProductType)
                                                .map(
                                                    type =>
                                                        !!team?.productTypes?.[
                                                            type
                                                        ] &&
                                                        `${t(type, {
                                                            ns: 'product',
                                                        })} ${
                                                            team.productTypes[
                                                                type
                                                            ] as number
                                                        }`
                                                )
                                                .filter(v => v)
                                                .join(' | ')}
                                        </Stack>
                                    </Stack>
                                </>
                            )}
                        </Box>
                    </Stack>

                    <ReasonModal
                        title="Are you sure you want to remove this team?"
                        content="Provide the reason for removing this team"
                        confirmText="Confirm"
                        open={!!deletingTeam}
                        isLoading={isRemoveTeamLoading}
                        handleCancel={() => setDeletingTeam(undefined)}
                        handleConfirm={reason => {
                            removeTeam({
                                teamId: deletingTeam as string,
                                reason,
                            });
                        }}
                    />

                    {inviteMember && (
                        <TeamModal
                            open
                            handleCancel={() => {
                                setInviteMember(false);
                            }}
                            team={{
                                name: team?.name as string,
                                id: team?.id as string,
                                isolated: false,
                            }}
                        />
                    )}
                </Grid>
            </Grid>
        );
    }
);
